/**
 * SSAudit — Audit Event Log Viewer
 * Displays sseventlogs entries with entity / date / user filters.
 * Read-only — append-only per data-lifecycle rules (§16).
 * ✅ Refactored to use shared SS component library (components/ss).
 * API: ssAuditAPI from ssApi.js
 */
import React, { useState, useEffect, useCallback } from 'react';
import { ssAuditAPI } from '../../services/ssApi';
import {
  Page, PageHeader, Card, Modal, Section,
  Button, Badge, Field, Input,
  Spinner, EmptyState, ErrorBanner,
  DataTable,
  EventTypeBadge, BreakGlassBadge,
  COLOR, FONT, RADIUS,
} from '../../components/ss';

function fmtDate(dt) {
  if (!dt) return '—';
  try { return new Date(dt).toLocaleString(); } catch { return dt; }
}

// ── Event Detail modal ────────────────────────────────────────────────────────
function EventDetail({ event, onClose }) {
  if (!event) return null;
  let payloadStr = '—';
  try {
    const raw = event.payload;
    const obj = typeof raw === 'string' ? JSON.parse(raw || '{}') : (raw ?? {});
    payloadStr = JSON.stringify(obj, null, 2);
  } catch { payloadStr = String(event.payload ?? '—'); }

  return (
    <Modal open={!!event} title="Event Detail" onClose={onClose} width={680}>
      <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr', rowGap: 10, fontSize: FONT.size.base, marginBottom: 20 }}>
        {[
          ['Event ID',    event.eventlogid || event.id],
          ['Event Type',  event.event_type],
          ['Entity',      event.entity_name ? `${event.entity_name} #${event.entity_id}` : '—'],
          ['Actor User',  event.actor_username || event.actor_userid || '—'],
          ['Break-Glass', event.is_breakglass ? 'Yes' : 'No'],
          ['Occurred At', fmtDate(event.occurred_at)],
          ['Correlation', event.correlation_id || '—'],
        ].map(([k, v]) => (
          <React.Fragment key={k}>
            <span style={{ fontWeight: FONT.weight.semi, color: COLOR.gray }}>{k}</span>
            <span style={{ color: COLOR.text, wordBreak: 'break-all' }}>{String(v ?? '—')}</span>
          </React.Fragment>
        ))}
      </div>
      <div style={{ fontWeight: FONT.weight.semi, fontSize: FONT.size.xs, color: COLOR.gray, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.6px' }}>Payload</div>
      <pre style={{ background: COLOR.grayBg, border: `1px solid ${COLOR.grayBorder}`, borderRadius: RADIUS.md, padding: 14, fontSize: 12, overflowX: 'auto', margin: 0, maxHeight: 320 }}>
        {payloadStr}
      </pre>
    </Modal>
  );
}

// ── Audit Tables panel ────────────────────────────────────────────────────────
function AuditTablesPanel({ open, onClose }) {
  const [tables, setTables]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr]         = useState('');

  useEffect(() => {
    if (!open) return;
    ssAuditAPI.tables()
      .then(d => setTables(d.data || d || []))
      .catch(ex => setErr(ex.response?.data?.detail || ex.message))
      .finally(() => setLoading(false));
  }, [open]);

  const COLS = [
    { key: 'tablename', label: 'Table', render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
    { key: 'insert', label: 'Insert', render: v => v ? <Badge color="green">Yes</Badge> : <Badge color="gray">No</Badge> },
    { key: 'update', label: 'Update', render: v => v ? <Badge color="green">Yes</Badge> : <Badge color="gray">No</Badge> },
    { key: 'delete', label: 'Delete', render: v => v ? <Badge color="red">Yes</Badge>   : <Badge color="gray">No</Badge> },
  ];

  return (
    <Modal open={open} title="Audited Tables (ssaudittables)" onClose={onClose} width={620}>
      <ErrorBanner>{err}</ErrorBanner>
      {loading ? <Spinner /> : (
        <DataTable columns={COLS} rows={tables} rowKey="tablename" emptyIcon="📋" emptyTitle="No audit table config found" />
      )}
    </Modal>
  );
}

// ── Table columns ─────────────────────────────────────────────────────────────
const getEventCols = (onView) => [
  {
    key: 'eventlogid', label: 'ID',
    render: v => <span style={{ fontFamily: 'monospace', fontSize: 12, color: COLOR.grayLight }}>{v || '—'}</span>,
  },
  {
    key: 'event_type', label: 'Event Type',
    render: v => <EventTypeBadge eventType={v} />,
  },
  {
    key: 'entity_name', label: 'Entity',
    render: (v, row) => v
      ? <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}{row.entity_id ? ` #${row.entity_id}` : ''}</span>
      : '—',
  },
  {
    key: 'actor_userid', label: 'Actor',
    render: (v, row) => row.actor_username || v || '—',
  },
  {
    key: 'is_breakglass', label: 'Break-Glass',
    render: v => <BreakGlassBadge isBreakGlass={v} />,
  },
  {
    key: 'occurred_at', label: 'Occurred At',
    render: v => <span style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmtDate(v)}</span>,
  },
  {
    key: '_view', label: '',
    render: (_, row) => (
      <Button variant="ghost" size="sm" onClick={e => { e.stopPropagation(); onView(row); }}>View →</Button>
    ),
  },
];

// ── Main ──────────────────────────────────────────────────────────────────────
export default function SSAudit() {
  const [events, setEvents]   = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');
  const [total, setTotal]     = useState(0);
  const [limit, setLimit]     = useState(100);
  const MAX_LIMIT = 500; // backend caps limit at 500

  const [filters, setFilters] = useState({ entity: '', id: '', from: '', to: '', event_type: '' });
  const [selected, setSelected]     = useState(null);
  const [showTables, setShowTables] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    const params = { limit, expand: true };
    if (filters.entity)     params.entity     = filters.entity;
    if (filters.id)         params.entity_id  = filters.id;
    if (filters.from)       params.from_date  = filters.from;
    if (filters.to)         params.to_date    = filters.to;
    if (filters.event_type) params.event_type = filters.event_type;

    ssAuditAPI.events(params)
      .then(d => {
        const rows = d.data || d || [];
        setEvents(Array.isArray(rows) ? rows : []);
        setTotal(d.meta?.total || rows.length);
        setErr('');
      })
      .catch(ex => { setErr(ex.response?.data?.detail || ex.message || 'Failed to load audit events'); setEvents([]); })
      .finally(() => setLoading(false));
  }, [filters, limit]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setLimit(100); }, [filters]);

  const upd = k => e => setFilters(f => ({ ...f, [k]: e.target.value }));
  const hasFilters = Object.values(filters).some(Boolean);

  return (
    <Page>
      <PageHeader
        title="Audit Event Log"
        subtitle={`Append-only record of every state change — sseventlogs.${total > 0 ? ` ${total.toLocaleString()} events.` : ''}`}
        action={<Button variant="ghost" onClick={() => setShowTables(true)}>📋 Audit Table Config</Button>}
      />

      <ErrorBanner>{err}</ErrorBanner>

      {/* Filters */}
      <Card style={{ marginBottom: 18, padding: '14px 18px' }}>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, alignItems: 'flex-end' }}>
          <Field label="Entity / Table" style={{ marginBottom: 0 }}>
            <Input style={{ width: 160 }} placeholder="e.g. ssparties" value={filters.entity} onChange={upd('entity')} />
          </Field>
          <Field label="Entity ID" style={{ marginBottom: 0 }}>
            <Input style={{ width: 100 }} placeholder="e.g. 42" value={filters.id} onChange={upd('id')} />
          </Field>
          <Field label="Event Type" style={{ marginBottom: 0 }}>
            <Input style={{ width: 200 }} placeholder="e.g. parties.party.created.v1" value={filters.event_type} onChange={upd('event_type')} />
          </Field>
          <Field label="From" style={{ marginBottom: 0 }}>
            <Input style={{ width: 160 }} type="datetime-local" value={filters.from} onChange={upd('from')} />
          </Field>
          <Field label="To" style={{ marginBottom: 0 }}>
            <Input style={{ width: 160 }} type="datetime-local" value={filters.to} onChange={upd('to')} />
          </Field>
          <div style={{ display: 'flex', gap: 8, alignSelf: 'flex-end' }}>
            <Button onClick={() => { setLimit(100); load(); }}>Search</Button>
            {hasFilters && <Button variant="ghost" onClick={() => setFilters({ entity: '', id: '', from: '', to: '', event_type: '' })}>Clear</Button>}
          </div>
        </div>
      </Card>

      {/* Table */}
      <Card>
        <DataTable
          columns={getEventCols(setSelected)}
          rows={events}
          rowKey={row => row.eventlogid || row.id || Math.random()}
          loading={loading}
          onRowClick={setSelected}
          selectedKey={selected?.eventlogid}
          emptyIcon="📜"
          emptyTitle={hasFilters ? 'No events match these filters.' : 'No audit events found.'}
          emptyMsg={hasFilters ? 'Try adjusting filters.' : 'Try searching with specific filters above.'}
          minWidth={700}
        />
        {!loading && events.length >= limit && limit < MAX_LIMIT && (
          <div style={{ textAlign: 'center', padding: '10px 0' }}>
            <Button variant="ghost" onClick={() => setLimit(l => Math.min(l + 100, MAX_LIMIT))}>
              Load more
            </Button>
          </div>
        )}
      </Card>

      <EventDetail event={selected} onClose={() => setSelected(null)} />
      <AuditTablesPanel open={showTables} onClose={() => setShowTables(false)} />
    </Page>
  );
}
